import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, LockOpen, History } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useActiveCompany, pickActiveYear } from "../../context/ActiveCompanyContext";
import PageHeader from "../../components/PageHeader";
import StatusBadge from "../../components/StatusBadge";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("sv-SE") : null);

export default function PeriodClosePage() {
  const { isAdmin } = useAuth();
  const [companies, setCompanies] = useState([]);
  const { companyId, setCompanyId, year, setYear } = useActiveCompany();
  const [years, setYears] = useState([]);
  const [periods, setPeriods] = useState(null);
  const [batches, setBatches] = useState([]);
  const [reopenTarget, setReopenTarget] = useState(null);
  const [reopenComment, setReopenComment] = useState("");
  const [historyTarget, setHistoryTarget] = useState(null);
  const [history, setHistory] = useState(null);
  const [busy, setBusy] = useState(false);

  const openHistory = async (p) => {
    setHistoryTarget(p);
    setHistory(null);
    try {
      const res = await api.get(`/periods/${p.id}/governance-history`);
      setHistory(res.data);
    } catch (e) {
      toast.error(apiError(e));
      setHistoryTarget(null);
    }
  };

  useEffect(() => {
    api.get("/companies")
      .then((c) => {
        setCompanies(c.data);
        if (c.data.length > 0 && !c.data.some((co) => co.id === companyId)) setCompanyId(c.data[0].id);
      })
      .catch((e) => toast.error(apiError(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/periods/years`)
      .then((res) => {
        setYears(res.data);
        const pick = pickActiveYear(res.data, year);
        if (pick !== year) setYear(pick);
      })
      .catch((e) => toast.error(apiError(e)));
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    if (!companyId || !year) return;
    setPeriods(null);
    try {
      const [p, b] = await Promise.all([
        api.get(`/companies/${companyId}/periods`, { params: { year } }),
        api.get("/actuals/batches", { params: { company_id: companyId, year } }),
      ]);
      setPeriods(p.data);
      setBatches(b.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => { load(); }, [companyId, year]); // eslint-disable-line react-hooks/exhaustive-deps

  const closePeriod = async (p) => {
    setBusy(true);
    try {
      await api.post(`/periods/${p.id}/close`);
      toast.success(`${MONTHS[p.month - 1]} closed — accounting actuals are now locked in`);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const confirmReopen = async () => {
    setBusy(true);
    try {
      await api.post(`/periods/${reopenTarget.id}/reopen`, { comment: reopenComment });
      toast.success(`${MONTHS[reopenTarget.month - 1]} reopened`);
      setReopenTarget(null);
      setReopenComment("");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const committedByMonth = {};
  batches.filter((b) => b.status === "COMMITTED").forEach((b) => { committedByMonth[b.month] = b; });

  return (
    <div data-testid="period-close-page">
      <PageHeader
        title="Period Close"
        subtitle="CLOSED is a management lock: the accepted monthly result is protected and reproducible. Corrections follow Reopen → correct → review → Close."
        actions={
          <div className="flex items-center gap-2">
            <Select value={companyId || ""} onValueChange={setCompanyId}>
              <SelectTrigger className="w-56" data-testid="pc-company-select">
                <SelectValue placeholder="Select company" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {years.length > 0 && (
              <Select value={year ? String(year) : ""} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger className="w-28" data-testid="pc-year-select"><SelectValue placeholder="Year" /></SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)} data-testid={`pc-year-${y}`}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        }
      />

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="pc-table">
        {!periods ? (
          <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : periods.length === 0 ? (
          <EmptyState title={`No periods for ${year}`} description="Create the financial year under Administration → Companies first." testId="pc-empty" />
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-secondary/50 border-b border-slate-200">
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-4">Month</th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-4">Period</th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-4">Actuals</th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-4">Committed import</th>
                <th className="py-2.5 px-4" />
              </tr>
            </thead>
            <tbody>
              {periods.map((p) => {
                const batch = committedByMonth[p.month];
                return (
                  <tr key={p.id} className={`border-b border-slate-100 ${p.status === "CLOSED" ? "bg-slate-50/60" : "hover:bg-accent/40"}`} data-testid={`pc-row-${p.month}`}>
                    <td className="py-2.5 px-4 text-sm font-medium text-slate-900">{MONTHS[p.month - 1]} {p.year}</td>
                    <td className="py-2.5 px-4">
                      <StatusBadge tone={p.status === "CLOSED" ? "slate" : "amber"} testId={`pc-status-${p.month}`}>
                        {p.status === "CLOSED" ? <><Lock className="h-3 w-3 mr-1 inline" />Closed</> : "Open"}
                      </StatusBadge>
                      {p.status === "CLOSED" && p.closed_at && (
                        <span className="block text-[11px] text-slate-400 mt-0.5" data-testid={`pc-closed-meta-${p.month}`}>
                          {p.closed_by_name ? `${p.closed_by_name} · ` : ""}{fmtDate(p.closed_at)}
                        </span>
                      )}
                      {p.status === "CLOSED" && p.group_overlay_status && (
                        <span
                          className={`inline-flex items-center mt-1 rounded px-1.5 py-0.5 text-[10px] font-medium border ${p.group_overlay_status === "PROVISIONAL" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}
                          title={p.group_overlay_status === "PROVISIONAL"
                            ? "Company-local results are closed. Group Overhead, recharge and 6591 remain provisional until the Group period is closed."
                            : "The Group period is closed — the accepted Group Overlay governs this month."}
                          data-testid={`pc-overlay-status-${p.month}`}
                        >
                          Group Overlay: {p.group_overlay_status === "PROVISIONAL" ? "Provisional" : "Accepted"}
                        </span>
                      )}
                      {p.reopen_comment && p.status === "OPEN" && (
                        <span className="block text-[11px] text-amber-700 mt-0.5" data-testid={`pc-reopen-note-${p.month}`}>
                          Reopened: {p.reopen_comment}
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 px-4">
                      <StatusBadge tone={p.actual_status === "VERIFIED_ACTUAL" ? "green" : "blue"} testId={`pc-actual-status-${p.month}`}>
                        {p.actual_status === "VERIFIED_ACTUAL" ? "Verified actual" : "Forecast"}
                      </StatusBadge>
                    </td>
                    <td className="py-2.5 px-4 text-xs text-slate-600" data-testid={`pc-batch-${p.month}`}>
                      {batch ? (
                        <>
                          <Link to={`/actuals/import?batch=${batch.id}`} className="text-[#0052CC] hover:underline font-medium">{batch.file_name}</Link>
                          <span className="block text-slate-400">{fmtDate(batch.committed_at)}</span>
                        </>
                      ) : (
                        <span className="text-slate-400">No committed import</span>
                      )}
                    </td>
                    <td className="py-2.5 px-4 text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" className="h-7 text-xs" asChild>
                          <Link to="/actuals/import" data-testid={`pc-import-link-${p.month}`}>Import P&L</Link>
                        </Button>
                        {(p.status === "CLOSED" || (p.close_history || []).length > 0 || (p.reopen_history || []).length > 0) && (
                          <Button variant="ghost" size="sm" className="h-7 text-xs" disabled={busy}
                            onClick={() => openHistory(p)} data-testid={`pc-history-${p.month}`}>
                            <History className="h-3 w-3 mr-1" /> History
                          </Button>
                        )}
                        {isAdmin && p.status === "OPEN" && p.actual_status === "VERIFIED_ACTUAL" && (
                          <Button size="sm" className="h-7 text-xs bg-[#0052CC] hover:bg-[#0047B3]" disabled={busy}
                            onClick={() => closePeriod(p)} data-testid={`pc-close-${p.month}`}>
                            <Lock className="h-3 w-3 mr-1" /> Close month
                          </Button>
                        )}
                        {isAdmin && p.status === "CLOSED" && (
                          <Button variant="outline" size="sm" className="h-7 text-xs" disabled={busy}
                            onClick={() => { setReopenTarget(p); setReopenComment(""); }} data-testid={`pc-reopen-${p.month}`}>
                            <LockOpen className="h-3 w-3 mr-1" /> Reopen
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!reopenTarget} onOpenChange={(v) => !v && setReopenTarget(null)}>
        <DialogContent data-testid="pc-reopen-dialog">
          <DialogHeader>
            <DialogTitle>Reopen {reopenTarget ? `${MONTHS[reopenTarget.month - 1]} ${reopenTarget.year}` : ""}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            Reopening removes the management lock so the month can be corrected. Existing actuals and committed
            imports remain in place — nothing is deleted or reset to forecast. After the correction, review the
            month and close it again. A reason is mandatory and is stored with your user and timestamp for audit.
          </p>
          <div className="space-y-1.5">
            <Label>Comment / reason</Label>
            <textarea
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm min-h-20"
              value={reopenComment}
              onChange={(e) => setReopenComment(e.target.value)}
              placeholder="Why is this month being reopened?"
              data-testid="pc-reopen-comment-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReopenTarget(null)} data-testid="pc-reopen-cancel">Cancel</Button>
            <Button className="bg-[#0052CC] hover:bg-[#0047B3]" disabled={busy || !reopenComment.trim()}
              onClick={confirmReopen} data-testid="pc-reopen-confirm">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
              Reopen month
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyTarget} onOpenChange={(v) => !v && setHistoryTarget(null)}>
        <DialogContent data-testid="pc-history-dialog">
          <DialogHeader>
            <DialogTitle>Close / reopen history — {historyTarget ? `${MONTHS[historyTarget.month - 1]} ${historyTarget.year}` : ""}</DialogTitle>
          </DialogHeader>
          {!history ? (
            <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
          ) : (
            <div className="space-y-2 text-sm" data-testid="pc-history-list">
              {history.snapshots.length === 0 && history.reopen_history.length === 0 && (
                <p className="text-slate-500">No close/reopen events recorded yet.</p>
              )}
              {history.snapshots.map((s) => (
                <div key={s.id} className="border border-slate-200 rounded-lg px-3 py-2" data-testid={`pc-history-snap-${s.close_seq}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-900">Accepted version {s.close_seq}</span>
                    <StatusBadge tone={s.state === "ACCEPTED" ? "slate" : "amber"} testId={`pc-history-state-${s.close_seq}`}>
                      {s.state === "ACCEPTED" ? "Accepted (current)" : "Reopened"}
                    </StatusBadge>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Closed by {s.closed_by_name || "unknown"} · {fmtDate(s.closed_at)}
                  </p>
                  {s.state === "REOPENED" && (
                    <p className="text-xs text-amber-700 mt-0.5">
                      Reopened by {s.reopened_by_name || "unknown"} · {fmtDate(s.reopened_at)} — {s.reopened_reason}
                    </p>
                  )}
                </div>
              ))}
              {history.reopen_history.map((e, i) => (
                <p key={i} className="text-xs text-slate-500" data-testid={`pc-history-reopen-${i}`}>
                  Reopen reason: “{e.comment}” — {e.reopened_by_name || "unknown"} · {fmtDate(e.reopened_at)}
                </p>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryTarget(null)} data-testid="pc-history-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
